/**
 * Reações de uma mensagem — puro, testável sem tela.
 *
 * O backend grava um mapa `reacoes` no doc da mensagem, com **uma entrada por
 * pessoa**:
 *
 *   { "123@lid": { emoji: "❤️", nome: "Jéssica" }, "clinica": { emoji: "👍" } }
 *
 * A tela precisa do contrário: **uma entrada por emoji**, com quantas pessoas o
 * usaram. É esta função que vira a chave do avesso.
 */

/**
 * Agrupa por emoji, na ordem em que cada emoji apareceu pela primeira vez.
 *
 * Ordem estável de propósito: ordenar por contagem faria os selos **pularem de
 * lugar** quando alguém reagisse, e o atendente clicaria no chip errado.
 *
 * @returns `[{ emoji, contagem, nomes }]` — `nomes` só com quem tem nome, para
 *   o `title` do chip.
 */
export function agruparReacoes(reacoes) {
  // Mensagem antiga não tem o campo. Um `??` aqui evita `?.` espalhado na UI.
  if (!reacoes || typeof reacoes !== 'object') return [];

  const porEmoji = new Map();

  for (const entrada of Object.values(reacoes)) {
    const emoji = String(entrada?.emoji ?? '').trim();
    // Entrada sem emoji não deveria existir — o backend apaga a chave em vez de
    // gravar vazio. Mas um documento meio gravado não pode virar chip fantasma,
    // que é exatamente o defeito que esta leva conserta.
    if (!emoji) continue;

    const atual = porEmoji.get(emoji) ?? { emoji, contagem: 0, nomes: [] };
    atual.contagem += 1;

    const nome = String(entrada?.nome ?? '').trim();
    if (nome) atual.nomes.push(nome);

    porEmoji.set(emoji, atual);
  }

  return [...porEmoji.values()];
}

/**
 * O texto do `title` de um chip: quem reagiu com aquele emoji.
 *
 * Sem nome nenhum (o `pushName` vem vazio às vezes), devolve `undefined` para o
 * atributo não ser renderizado — um `title=""` cria uma tarja preta vazia no
 * hover, pior que não ter dica.
 */
export function nomesDoChip({ nomes, contagem }) {
  if (!nomes?.length) return undefined;
  const lista = nomes.join(', ');
  // Alguém sem nome no meio: dizer "e mais 2" é honesto sobre o que não se sabe.
  const anonimos = contagem - nomes.length;
  return anonimos > 0 ? `${lista} e mais ${anonimos}` : lista;
}

/**
 * O que enviar ao clicar num emoji da paleta — a regra do WhatsApp.
 *
 * É a lógica que o atendente SENTE, e a que mais se erra escrevendo à mão
 * dentro de um componente. Fica aqui, testável sem tela.
 *
 * - Clicar no emoji que a clínica JÁ usou → `''` (remove). Reagir duas vezes com
 *   o mesmo emoji não faz sentido, e sem isto não haveria como desfazer.
 * - Clicar em outro → esse outro (substitui; o backend sobrescreve a chave).
 * - Sem reação ainda → o escolhido.
 *
 * @param atual     o emoji que a clínica já pôs, ou null/undefined
 * @param escolhido o que acabou de ser clicado
 */
export function escolherReacao(atual, escolhido) {
  return atual === escolhido ? '' : escolhido;
}
